<template>
  <div v-if="!isSaved" class="not-saved bg-white">
    <div class="sms-message-box">
      <div class="editor">
        <editor-content :editor="editor" />
      </div>
      <div v-if="filterTags?.length" class="parameters">
        <span><strong>הוספת פרמטרים להודעה:</strong></span>
        <div class="flex flex-row mt-2">
          <span>כתבו '@' או בחרו מהרשימה: </span>
          <div class="flex gap-1 flex-wrap mr-1">
            <span
              v-for="tag in filteredSmsParametersList"
              :key="tag"
              class="mention h-8"
              @click="addTag(tag)">
          {{ tag }}
        </span>
          </div>
        </div>
      </div>
    </div>
    <div class="action-buttons-panel">
      <Button label="ניקוי" class="p-button-outlined p-button-sm p-button-secondary" @click="clearMessage" />
      <Button class="p-button-sm" label="שמירה" @click="saveSmsTemplate" />
    </div>
  </div>
  <div v-else class="saved bg-white">
    <span class="sms-message" style="line-height: 1.85rem" v-html="smsMessage"></span>
    <Divider />
    <Button
      label=" עריכה "
      style="flex-grow:1"
      icon="pi pi-pencil"
      class="p-button-text p-button-sm"
      @click="isSaved=false" />
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, PropType, ref } from 'vue';
import { Editor, EditorContent, VueRenderer } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import { Mention } from '@tiptap/extension-mention';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import tippy, { Instance } from 'tippy.js';
import ParametersList from './ParametersList.vue';
import { useToast } from 'vue-toastification';
import { SmsParameters, smsParametersList } from '@torim-app/types';

const props = defineProps({
  preMessage: {
    type: String,
    required: false
  },
  filterTags: {
    type: Array as PropType<Array<SmsParameters>>,
    required: false,
    default: undefined
  },
  isSaved: {
    type: Boolean,
    required: false,
    default: false
  }

});
const emit = defineEmits(['saveMessage']);

const toast = useToast();

const editor = ref<Editor>();
const placeholderMessage = `<p><span style="color:#b6b6b6"><em>היי, מה נשמע?<br/>החגים מתקרבים, וזה הזמן להזמין תור ללק ג'ל שיעיף את המשפחה באוויר 🔥!<em></span></p>`;
const genericPlaceholder = '<p><span style="color:#b6b6b6">מה תרצו לשלוח?</span></p>';
const isPlaceholder = ref(!props.preMessage);
const isSaved = ref(props.isSaved);
const filteredSmsParametersList = props.filterTags !== undefined ? smsParametersList.filter(x => props.filterTags?.includes(x)) : smsParametersList;

const smsMessage = computed(() => editor.value ? editor.value.getHTML() : '');

const clearMessage = () => {
  if (!editor.value) return;

  editor.value.commands.setContent(genericPlaceholder);
  isPlaceholder.value = true;
  isSaved.value = false;
};

const addTag = (tag: string) => {
  if (editor.value) {
    const current = editor.value.getHTML();
    editor.value.commands.clearContent();

    if (!isPlaceholder.value)
      editor.value.commands.insertContent(current);

    editor.value.commands.insertContent([
      { type: 'text', text: ' ' },
      {
        type: 'mention',
        attrs: {
          label: `${tag}`
        }
      }
    ]);

    isPlaceholder.value = false;
  }
};

const saveSmsTemplate = () => {
  if (isPlaceholder.value) {
    toast.error('לא ניתן לשלוח הודעה ריקה');
    return;
  }

  if (editor.value) {
    console.log(editor.value.getText());
    emit('saveMessage', editor.value.getText());
    isSaved.value = true;
  }
};

onMounted(() => {
  editor.value = new Editor({
    content: props.preMessage ?? placeholderMessage,
    extensions: [
      StarterKit,
      TextStyle,
      Mention.configure({
        HTMLAttributes: {
          class: 'mention'
        },
        renderLabel({ options, node }) {
          return `${node.attrs.label ?? node.attrs.id}`;
        },
        suggestion: {
          items: ({ query }) => {
            return filteredSmsParametersList.filter(item => item.startsWith(query)).slice(0, 5);
          },

          render: () => {
            let component: VueRenderer;
            let popup: Array<Instance>;

            return {
              onStart: props => {
                component = new VueRenderer(ParametersList, {
                  props,
                  editor: props.editor
                });

                if (!props.clientRect)
                  return;

                //@ts-ignore
                popup = tippy('body', {
                  getReferenceClientRect: props.clientRect,
                  appendTo: () => document.body,
                  content: component.element,
                  showOnCreate: true,
                  interactive: true,
                  trigger: 'manual',
                  placement: 'bottom-start'
                });
              },

              onUpdate(props) {
                component.updateProps(props);

                if (!props.clientRect) return;

                popup[0].setProps({
                  //@ts-ignore
                  getReferenceClientRect: props.clientRect
                });
              },

              onKeyDown(props) {
                if (props.event.key === 'Escape') {
                  popup[0].hide();

                  return true;
                }

                return component.ref?.onKeyDown(props);
              },

              onExit() {
                popup && popup[0].destroy();
                component.destroy();
              }
            };
          }
        }
      }),
      Color.configure({
        types: ['textStyle']
      })
    ],
    onFocus({ editor }) {
      if (isPlaceholder.value) {
        editor.commands.setContent(null);
        isPlaceholder.value = false;
      }
      editor.commands.setColor('#000000');
    },
    onBlur({ editor }) {
      const current = editor.getText().trim();
      if (!current || current === '') {
        clearMessage();
      }
    }
  });
  editor.value.commands.setColor('#b6b6b6');
});

onBeforeUnmount(() => {
  if (editor.value)
    editor.value.destroy();
});

</script>

<style lang="scss">
@use "../../../../../assets/css/vars" as *;

.mention {
  border-radius: 0.5rem;
  padding: 0.25rem 0.5rem;
  background-color: $lead-color-lighten;
  color: $lead-color-darker;
  width: max-content;
}

.not-saved {
  .sms-message-box {
    border: 1px solid $light-gray;
    min-height: 4rem;
    border-radius: 0.4rem;
    display: flex;
    flex-direction: column;

    .editor {
      padding: 0.75rem;
      flex-grow: 1;

      .ProseMirror {
        line-height: 2rem;

        p:first-child {
          margin-top: 0;
        }

        p:last-child {
          margin-bottom: 0;
        }
      }
    }

    .parameters {
      padding: 0.75rem;
      width: 100%;
      display: flex;
      flex-direction: column;
      border-top: 1px solid $light-gray;

      .tags-list {
        margin-top: 0.5rem;
        display: flex;
        justify-content: flex-start;
        align-items: center;
        gap: 0.5rem;

        span:first-child {
          cursor: default;
          width: 50%;
        }
      }
    }
  }

  .action-buttons-panel {
    width: 100%;
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    margin-top: 1rem;
  }
}

.saved {
  padding: 1rem 0.75rem 0.5rem 0.75rem;
  border-radius: 0.4rem;
  border: 1px solid $light-gray;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;

  .p-divider {
    margin: 0.75rem;
  }

  .p-button {
    color: black;

    .p-button-icon {
      margin-left: 0.5rem;
    }
  }
}

</style>
